export type Locale = "fi" | "en" | "sv";

export interface SlugTranslation {
    fi: string;
    en: string;
    sv: string;
}

export const routeConfig: Record<string, SlugTranslation> = {
    home: { fi: "", en: "", sv: "" },
    osta: { fi: "osta", en: "buy", sv: "kop" },
    palvelu: { fi: "palvelu", en: "service", sv: "tjanst" },
    senioritabletti: { fi: "senioritabletti", en: "senior-tablet", sv: "seniorsurfplatta" },
    senioripuhelin: { fi: "senioripuhelin", en: "senior-phone", sv: "seniortelefon" },
    turvaranneke: { fi: "turvaranneke", en: "safety-bracelet", sv: "trygghetslarm" },
    videopuhelu_appi: { fi: "videopuhelu_appi", en: "video-call-app", sv: "videosamtals-app" },
    lisatarvikkeet: { fi: "lisatarvikkeet", en: "accessories", sv: "tillbehor" },
    muistipeli: { fi: "muistipeli", en: "memory-game", sv: "minnesspel" },
    usein_kysyttya: { fi: "usein_kysyttya", en: "faq", sv: "vanliga-fragor" },
    yhteydenotto: { fi: "yhteydenotto", en: "contact", sv: "kontakt" },
    yrityksille: { fi: "yrityksille", en: "for-businesses", sv: "for-foretag" },
    happysenior_blogi: { fi: "happysenior_blogi", en: "happysenior-blog", sv: "happysenior-blogg" },
    aboutus: { fi: "aboutus", en: "about-us", sv: "om-oss" },
    palveluehdot: { fi: "palveluehdot", en: "terms-of-service", sv: "anvandarvillkor" },
    copyright: { fi: "copyright", en: "privacy-policy", sv: "integritetspolicy" },
    ostoskori: { fi: "ostoskori", en: "shopping-cart", sv: "varukorg" },
    toimitus: { fi: "toimitus", en: "shipping", sv: "leverans" },
    maksutapa: { fi: "maksutapa", en: "payment-method", sv: "betalningsmetod" },
    tee_tilaus: { fi: "tee_tilaus", en: "place-order", sv: "lagg-bestallning" },
    thanks: { fi: "thanks", en: "thank-you", sv: "tack" },
    kirjaudu: { fi: "kirjaudu", en: "login", sv: "logga-in" },
    luo_tili: { fi: "luo_tili", en: "register", sv: "skapa-konto" },
    tili: { fi: "tili", en: "account", sv: "konto" },
    // tili tab IDs
    tilaus: { fi: "tilaus", en: "order", sv: "bestallning" },
    asiakas: { fi: "asiakas", en: "customer", sv: "kund" },
    seniori: { fi: "seniori", en: "senior", sv: "senior" },
    laheinen: { fi: "laheinen", en: "relative", sv: "anhorig" },
    "salasana/vaihda": { fi: "salasana/vaihda", en: "password/reset", sv: "losenord/aterstall" },
    subscription_end: { fi: "subscription_end", en: "subscription-ended", sv: "prenumeration-avslutad" },
    // Article dynamic routes — prefix differs from the list page (happysenior_blogi)
    blog: { fi: "blog", en: "happysenior-blog", sv: "happysenior-blogg" },
};

/** Type-safe route key — TypeScript will reject unknown strings */
export type RouteKey = keyof typeof routeConfig;

/** Extract locale from a pathname string (e.g. "/en/buy" → "en", "/osta" → "fi").
 *
 *  Resolution order:
 *  1. URL prefix (/en/, /sv/) — authoritative when present
 *  2. localStorage("language") — user's explicit choice via region selector
 *  3. Default: "fi"
 *
 *  localStorage is safe here because it's ONLY written by LanguageChangeDialog.applyChange
 *  after user explicitly confirms a language switch. It represents user intent. */
export function getLocaleFromPath(pathname?: string): Locale {
    const p = pathname || window.location.pathname;
    const first = p.split("/")[1];
    if (first === "en" || first === "sv") return first;
    const stored = localStorage.getItem("language") as Locale | null;
    if (stored === "en" || stored === "sv") return stored;
    return "fi";
}

/** App handles URL slug translation, Weglot handles DOM text only */
export const TRANSLATED_SLUGS = true;

export function getSlug(routeKey: string, locale: Locale): string {
    return routeConfig[routeKey]?.[locale] ?? routeKey;
}

export function getLocalizedPath(routeKey: string, locale: Locale): string {
    const slug = getSlug(routeKey, locale);
    const base = slug ? `/${slug}` : "/";
    if (locale === "fi") return base;
    return slug ? `/${locale}/${slug}` : `/${locale}`;
}

export function getRouteKeyFromSlug(slug: string, locale: Locale): string | null {
    for (const [key, translations] of Object.entries(routeConfig)) {
        if (translations[locale] === slug) {
            return key;
        }
    }
    return null;
}

/** Strip locale prefix from pathname: "/en/buy" → "/buy", "/osta" → "/osta" */
export function stripLocalePrefix(pathname: string): string {
    const match = pathname.match(/^\/(en|sv)(\/.*)?$/);
    return match ? (match[2] || "/") : pathname;
}

/**
 * Returns the localized path for a route key based on the current browser URL.
 * Plain function — no React, safe to use in event handlers and Redux sagas.
 *
 * Examples:
 *   localePath("osta")              → "/osta"           (fi)
 *   localePath("osta")              → "/en/buy"         (en)
 *   localePath("tili", "tilaus")    → "/en/account/order" (en)
 */
export function localePath(key: RouteKey, extra?: string): string {
    const locale = getLocaleFromPath(window.location.pathname);
    const slug = TRANSLATED_SLUGS ? getSlug(key, locale) : getSlug(key, "fi");
    const base = slug ? `/${slug}` : "/";
    const prefix = locale === "fi" ? "" : `/${locale}`;
    if (!extra) return prefix ? `${prefix}${base}` : base;
    const translatedExtra = TRANSLATED_SLUGS ? getSlug(extra, locale) : extra;
    return `${prefix}${base}/${translatedExtra}`;
}

export function buildTranslatedPath(pathname: string, newLang: string, currentLang: string): string {
    const newLocale = newLang as Locale;
    const curLocale = currentLang as Locale;
    const bare = stripLocalePrefix(pathname);
    if (bare.startsWith("/admin")) return bare;
    const bareSlug = bare.replace(/^\/+/, "");
    const wholePathKey = getRouteKeyFromSlug(bareSlug, curLocale)
        || getRouteKeyFromSlug(bareSlug, "fi")
        || getRouteKeyFromSlug(bareSlug, "en")
        || getRouteKeyFromSlug(bareSlug, "sv");
    const prefix = TRANSLATED_SLUGS && newLocale !== "fi" ? `/${newLocale}` : "";
    if (wholePathKey) {
        const translatedWholePath = getSlug(wholePathKey, newLocale);
        return `${prefix}/${translatedWholePath}`;
    }
    const segments = bare.split("/").filter(Boolean);
    if (TRANSLATED_SLUGS && segments.length > 0) {
        const translated = segments.map((seg) => {
            const key = getRouteKeyFromSlug(seg, curLocale)
                || getRouteKeyFromSlug(seg, "fi")
                || getRouteKeyFromSlug(seg, "en")
                || getRouteKeyFromSlug(seg, "sv");
            if (key) return getSlug(key, newLocale);
            return seg;
        }).join("/");
        return `${prefix}/${translated}`;
    }
    return prefix ? `${prefix}${bare}` : bare;
}

